import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaPenNib,
  FaMagnifyingGlass,
  FaChartColumn,
  FaBookOpen,
  FaFileCircleCheck,
  FaToolbox,
  FaRobot,
  FaBrain,
  FaBoltLightning,
  FaArrowRight,
} from "react-icons/fa6";
import PageHero from "../components/PageHero";

const STAGES = [
  { icon: <FaPenNib />, title: "Research design", text: "Define the question, scope the study, and choose methods that fit your context, resources, and timeline." },
  { icon: <FaMagnifyingGlass />, title: "Data collection", text: "Plan instruments, recruit participants, and structure clean, ethical, well-documented data capture." },
  { icon: <FaChartColumn />, title: "Data analysis", text: "Apply quantitative and qualitative analysis with reproducible workflows you can defend and reuse." },
  { icon: <FaBookOpen />, title: "Writing for publication", text: "Build manuscripts that clearly tell your study's story and target the right journals for your audience." },
  { icon: <FaFileCircleCheck />, title: "Peer review preparation", text: "Strengthen submissions before you send them — and respond effectively to reviewer feedback when it lands." },
  { icon: <FaToolbox />, title: "Tools & workflow", text: "Streamline tooling, file structure, and habits — and bring AI into the workflow without losing rigor or reproducibility." },
];

const AI_TRACKS = [
  { icon: <FaBrain />, title: "Integrating AI into research", text: "Practical use across the research cycle — literature review, drafting, data exploration, code, and synthesis — with attention to ethics, attribution, and reproducibility." },
  { icon: <FaRobot />, title: "AI-augmented workflows", text: "Combine AI tools with the rest of your stack so the work stays organized, version-controlled, and defensible from first draft to final submission." },
  { icon: <FaBoltLightning />, title: "Productivity for researchers", text: "Focused habits, templates, and tooling that compound across projects — so you spend more time on the thinking and less on the busywork." },
];

const WORKSHOP_AUDIENCES = [
  { t: "Students", d: "Build confidence with the research process — from your first project through your first publication." },
  { t: "Early-career researchers", d: "Strengthen design, analysis, and writing habits while output expectations grow." },
  { t: "Grant writers", d: "Sharpen proposal narratives, methods sections, and reviewer-ready argumentation across funding cycles." },
  { t: "Journal & conference teams", d: "Improve submission quality, review workflows, and author guidance with better systems and tooling." },
];

export default function Research() {
  useEffect(() => {
    document.title = 'Research Support — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Research Workshops"
        title={
          <>
            Research support, from{" "}
            <span className="italic text-brand-deep">first study</span> to{" "}
            <span className="italic text-brand-aubergine">established work.</span>
          </>
        }
        lead="Targeted workshops and one-on-one guidance for students, early-career researchers, and grant writers — including how to integrate AI into the research process and everyday workflows. Applied, practical, and built around your real projects."
      />

      {/* Stages timeline */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="rounded-3xl bg-white border border-ink/10 overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-ink/10">
              {STAGES.map((s, i) => (
                <div
                  key={s.title}
                  className={`p-7 ${i >= 3 ? "sm:border-t sm:border-ink/10" : ""}`}
                >
                  <div className="flex items-center justify-end">
                    <span className="text-brand-deep text-xl">{s.icon}</span>
                  </div>
                  <h3 className="mt-4 font-display text-xl text-ink">{s.title}</h3>
                  <p className="mt-2 text-slate2 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI in research */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-7">
              <div className="eyebrow eyebrow-bar">AI in Research</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                Workshops on integrating AI into the research process — without losing rigor.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate2 leading-relaxed">
                Generative AI is changing how research gets done. We help researchers
                use it deliberately — in ways that strengthen the work, respect the
                ethics, and stay reproducible.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {AI_TRACKS.map((t) => (
              <div
                key={t.title}
                className="bg-white rounded-2xl border border-ink/10 p-7"
              >
                <span className="text-brand-aubergine text-xl">{t.icon}</span>
                <h3 className="mt-5 font-display text-xl text-ink">{t.title}</h3>
                <p className="mt-2 text-slate2 leading-relaxed">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="eyebrow eyebrow-bar">Who it's for</div>
              <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
                Workshops calibrated to where you are in your research career.
              </h2>
              <p className="mt-5 text-slate2 leading-relaxed">
                Sessions run as cohorts, single-team workshops, or bespoke
                engagements with research groups, departments, and the teams who
                support the publishing and conference sides of academic life.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {WORKSHOP_AUDIENCES.map((a) => (
                <div
                  key={a.t}
                  className="bg-white rounded-2xl border border-ink/15 p-7 shadow-soft"
                >
                  <div className="h-px w-10 bg-brand-deep mb-4" />
                  <h3 className="font-display text-xl text-ink">{a.t}</h3>
                  <p className="mt-2 text-slate2 leading-relaxed">{a.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bone">
        <div className="container-page pb-20 pt-16">
          <div className="rounded-3xl bg-ink text-bone p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="eyebrow !text-brand-sage">
                <span className="inline-block h-px w-8 bg-brand-sage mr-2 align-middle" />
                Plan a workshop
              </div>
              <h3 className="mt-3 font-display text-2xl lg:text-3xl leading-tight">
                Bring a research workshop to your team.
              </h3>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-bone text-ink font-medium px-6 py-3 rounded-full hover:bg-cream transition-colors"
            >
              Request a workshop <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
